import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { toast } from "sonner";

/**
 * Bulk auto-fix dialog: iterates over recipes missing AI images and regenerates
 * each one sequentially with a progress bar + abort button.
 *
 * Props:
 *   - open / onOpenChange
 *   - targets: array of recipes missing image — passed in so parent can keep them in sync
 *   - onRecipeImageSet(recipeId): notify parent each time an image is generated
 */
export default function AutoFixImagesDialog({ open, onOpenChange, targets, onRecipeImageSet }) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0, currentTitle: "" });
  const abortRef = useRef(false);

  const handleOpenChange = (o) => {
    // Block close while running
    if (!running) onOpenChange(o);
  };

  const run = async () => {
    if (running) return;
    if (!targets || targets.length === 0) {
      toast.info("Alle oppskrifter har allerede AI-bilde");
      onOpenChange(false);
      return;
    }
    abortRef.current = false;
    setRunning(true);
    setProgress({ done: 0, total: targets.length, failed: 0, currentTitle: "" });

    let done = 0;
    let failed = 0;
    for (const rec of targets) {
      if (abortRef.current) break;
      setProgress({ done, total: targets.length, failed, currentTitle: rec.title });
      try {
        const res = await api.regenerateRecipeImage(rec.id);
        if (onRecipeImageSet) onRecipeImageSet(rec.id, !!res.data?.image_url);
      } catch {
        failed += 1;
      }
      done += 1;
      setProgress({ done, total: targets.length, failed, currentTitle: rec.title });
    }
    setRunning(false);
    if (abortRef.current) {
      toast.info(`Avbrutt etter ${done} av ${targets.length} bilder`);
    } else {
      toast.success(`${done - failed} bilder generert${failed > 0 ? ` (${failed} feilet)` : ""}`);
    }
  };

  const abort = () => { abortRef.current = true; };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-sm" data-testid="auto-fix-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Auto-fiks bilder</DialogTitle>
        </DialogHeader>
        {!running ? (
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              <strong>{targets?.length || 0}</strong> oppskrifter mangler AI-generert bilde.
            </p>
            <p className="text-xs text-stone-500">
              Hver bildegenerering tar 15-30 sek. Total tid: ca. {Math.ceil((targets?.length || 0) * 0.5)}-{targets?.length || 0} minutter.
              Du kan avbryte underveis. Bildene trekker fra Universal Key-saldoen.
            </p>
            <div className="flex gap-2">
              <button onClick={() => onOpenChange(false)} className="flex-1 rounded-md border border-stone-200 px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50" data-testid="auto-fix-cancel">
                Avbryt
              </button>
              <button onClick={run} className="flex-1 rounded-md bg-navy-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-800" data-testid="auto-fix-start">
                Start
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-stone-700">Fremgang</span>
                <span className="text-sm text-stone-500">{progress.done} av {progress.total}</span>
              </div>
              <div className="h-2 w-full bg-stone-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-seawater-600 transition-all duration-300"
                  style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
            {progress.currentTitle && (
              <p className="text-xs text-stone-500 truncate">Genererer: <span className="font-medium text-stone-700">{progress.currentTitle}</span></p>
            )}
            {progress.failed > 0 && (
              <p className="text-xs text-amber-600">{progress.failed} feilet</p>
            )}
            <button onClick={abort} className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-100" data-testid="auto-fix-abort">
              <X className="h-4 w-4" /> Avbryt
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
